/**
 * SLAMSettings — Full Platform Configuration
 *
 * Tabs:
 *  1. Account        — identity, role
 *  2. Platform       — default sport + team
 *  3. Navigation     — sidebar layout
 *  4. Evaluation     — performance criteria (AI-injected)
 *  5. AI Model       — position weights, gaps, thresholds, feedback loops
 *  6. Team           — staff roster, invite, role assignment (AD)
 *  7. Governance     — role-based feature flags (AD)
 *  8. Activity       — user event timeline
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  User, Settings, LayoutDashboard, Target, Save, CheckCircle, Loader2,
  Search, ChevronRight, Brain, Lock, Users, Shield, Activity,
  Plus, Trash2, Edit3, Mail, RefreshCw, Download, ToggleLeft,
  ToggleRight, Sliders, TrendingUp, ThumbsUp, ThumbsDown, Clock,
  Filter, ChevronDown, AlertCircle, Crown, Eye, EyeOff,
} from 'lucide-react';
import axios from 'axios';
import { NODE_BACKEND_URL } from '../../utils/constant';
import { getAuthToken, getSetting, getUser, getUserSettings } from '../../utils/helpers';
import { useTeam } from '../../lib/teamConfig';
import { getTeamsBySportCode } from '../../lib/teamConfig/teams';
import { fetchNatStatTeams, isNatStatBasketball } from '../../utils/natstatDataService';
import NavigationSettings from './NavigationSettings';

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  'Head Coach', 'Assistant Coach', 'Analytics Director',
  'Recruiting Coordinator', 'General Manager', 'Scout', 'Athletic Director', 'Other',
];

const GOVERNANCE_ROLES = [
  { id: 'AD',             label: 'Athletic Director', color: 'amber',  icon: Crown },
  { id: 'headCoach',      label: 'Head Coach',        color: 'blue',   icon: Users },
  { id: 'assistantCoach', label: 'Assistant Coach',   color: 'indigo', icon: Users },
  { id: 'analyst',        label: 'Analyst',           color: 'green',  icon: TrendingUp },
  { id: 'recruiter',      label: 'Recruiter',         color: 'purple', icon: Search },
  { id: 'player',         label: 'Player',            color: 'zinc',   icon: User },
];

const FEATURE_FLAGS = [
  { id: 'videoAnalytics', label: 'Video Analytics',  desc: 'Film analysis, segment review, game dashboards' },
  { id: 'askSlamAI',      label: 'Ask SLAM AI',      desc: 'AI chat assistant with NL2SQL and tools' },
  { id: 'gmDashboard',    label: 'GM Dashboard',     desc: 'Roster management, player ratings, gaps' },
  { id: 'transferPortal', label: 'Transfer Portal',  desc: 'Portal watch, DraftExpress data, fit scores' },
  { id: 'nilDashboard',   label: 'NIL Dashboard',    desc: 'Revenue sharing, market valuations' },
  { id: 'gameFilm',       label: 'Game Film',        desc: 'Raw game film and clipping tools' },
  { id: 'wojChat',        label: 'Woj Chat',         desc: 'Breaking news and trade analysis' },
  { id: 'exportData',     label: 'Export Data',      desc: 'CSV/PDF exports of analytics' },
  { id: 'teamManagement', label: 'Team Management',  desc: 'Invite staff, assign roles (AD only)' },
  { id: 'governance',     label: 'Governance',       desc: 'Feature flags per role tier (AD only)' },
  { id: 'aiModel',        label: 'AI Model Config',  desc: 'Position weights, feedback loops (AD only)' },
];

const POSITIONS = ['PG', 'SG', 'SF', 'PF', 'C'];
const POSITION_ATTRS = ['scoring', 'defense', 'athleticism', 'iq', 'playmaking'];

const SPORT_OPTIONS = [
  { code: 'MBB',  name: "Men's Basketball",   emoji: '🏀' },
  { code: 'WBB',  name: "Women's Basketball", emoji: '🏀' },
  { code: 'NBA',  name: 'NBA',                emoji: '🏀' },
  { code: 'WNBA', name: 'WNBA',               emoji: '🏀' },
  { code: 'EURO', name: 'EuroLeague',         emoji: '🇪🇺' },
  { code: 'GL',   name: 'G-League',           emoji: '🏀' },
  { code: 'MLB',  name: 'MLB',                emoji: '⚾' },
  { code: 'CBB',  name: 'College Baseball',   emoji: '⚾' },
];

const NAV_SECTIONS = [
  { id: 'account',    label: 'Account',    icon: User },
  { id: 'platform',   label: 'Platform',   icon: Settings },
  { id: 'navigation', label: 'Navigation', icon: LayoutDashboard },
  { id: 'evaluation', label: 'Evaluation', icon: Brain },
  { id: 'ai-model',   label: 'AI Model',   icon: Sliders },
  { id: 'team',       label: 'Team',       icon: Users },
  { id: 'governance', label: 'Governance', icon: Shield },
  { id: 'portal',     label: 'Portal',     icon: Target },
  { id: 'activity',   label: 'Activity',   icon: Activity },
];

const API = (path) => `${NODE_BACKEND_URL}${path}`;
const authHeader = () => ({ Authorization: getAuthToken() });

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  card:         'bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-5',
  label:        'text-[10px] font-semibold text-[#666666] uppercase tracking-widest mb-1.5 block',
  input:        'w-full bg-[#111111] border border-[#222222] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[rgba(232,184,0,0.5)] transition-colors',
  sectionTitle: 'text-sm font-semibold text-white mb-0.5',
  sectionSub:   'text-xs text-[#555555] mb-5',
  btn:          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
  btnGold:      'bg-[#E8B800] text-black hover:bg-[#f0c000]',
  btnGhost:     'border border-[#2A2A2A] text-[#888888] hover:text-white hover:border-[#444]',
  btnDanger:    'border border-red-900/40 text-red-500 hover:bg-red-900/20',
  pill:         'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SLAMSettings = () => {
  const { switchTeam } = useTeam();
  const [activeSection, setActiveSection]   = useState('account');
  const [isSaving, setIsSaving]             = useState(false);
  const [saved, setSaved]                   = useState(false);

  // Account
  const user = getUser();
  const [role, setRole] = useState(() => localStorage.getItem('slam_user_role') || '');

  // Platform
  const setting = getSetting();
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedTeam, setSelectedTeam]   = useState(null);
  const [teams, setTeams]                 = useState([]);
  const [loadingTeams, setLoadingTeams]   = useState(false);
  const [teamSearch, setTeamSearch]       = useState('');

  // Evaluation
  const [performanceCriteria, setPerformanceCriteria] = useState('');

  // AI Model
  const [aiModel, setAiModel]         = useState(null);
  const [aiModelLoading, setAiModelLoading] = useState(false);
  const [feedbackStats, setFeedbackStats]   = useState(null);

  // Team Management
  const [teamMembers, setTeamMembers]   = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [teamLoading, setTeamLoading]   = useState(false);
  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteRole, setInviteRole]     = useState('assistantCoach');
  const [inviteName, setInviteName]     = useState('');
  const [inviting, setInviting]         = useState(false);

  // Governance
  const [governance, setGovernance]         = useState(null);
  const [governanceLoading, setGovernanceLoading] = useState(false);

  // Portal Config
  const [portalConfig, setPortalConfig]       = useState(null);
  const [portalConfigLoading, setPortalConfigLoading] = useState(false);
  const [portalConfigSaving, setPortalConfigSaving]   = useState(false);

  // Activity
  const [activityEvents, setActivityEvents] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  const [activityPage, setActivityPage]     = useState(0);
  const [activityTotal, setActivityTotal]   = useState(0);

  // ── Hydrate ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const savedSport = sessionStorage.getItem('selectedSport');
    if (savedSport) { const p = JSON.parse(savedSport); setSelectedSport(p); fetchTeams(p); }
    const savedTeam = sessionStorage.getItem('selectedTeam');
    if (savedTeam) setSelectedTeam(JSON.parse(savedTeam));
    if (setting?.performance_criteria) setPerformanceCriteria(setting.performance_criteria);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load section data when tab changes
  useEffect(() => {
    if (activeSection === 'ai-model' && !aiModel) loadAiModel();
    if (activeSection === 'team' && teamMembers.length === 0) loadTeam();
    if (activeSection === 'governance' && !governance) loadGovernance();
    if (activeSection === 'portal' && !portalConfig) loadPortalConfig();
    if (activeSection === 'activity') loadActivity(0);
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loaders ─────────────────────────────────────────────────────────────────
  const loadAiModel = async () => {
    setAiModelLoading(true);
    try {
      const [modelRes, statsRes] = await Promise.all([
        axios.get(API('/api/settings/ai-model'), { headers: authHeader() }),
        axios.get(API('/api/settings/feedback-stats'), { headers: authHeader() }),
      ]);
      if (modelRes.data.success) {
        setAiModel(modelRes.data.data);
        if (modelRes.data.data.performanceCriteria) {
          setPerformanceCriteria(modelRes.data.data.performanceCriteria);
        }
      }
      if (statsRes.data.success) setFeedbackStats(statsRes.data.data);
    } catch (e) { console.error('[settings] ai-model load:', e.message); }
    finally { setAiModelLoading(false); }
  };

  const loadTeam = async () => {
    setTeamLoading(true);
    try {
      const res = await axios.get(API('/api/settings/team'), { headers: authHeader() });
      if (res.data.success) {
        setTeamMembers(res.data.data || []);
        setPendingInvites(res.data.invites || []);
      }
    } catch (e) { console.error('[settings] team load:', e.message); }
    finally { setTeamLoading(false); }
  };

  const loadGovernance = async () => {
    setGovernanceLoading(true);
    try {
      const res = await axios.get(API('/api/settings/governance'), { headers: authHeader() });
      if (res.data.success) setGovernance(res.data.data);
    } catch (e) { console.error('[settings] governance load:', e.message); }
    finally { setGovernanceLoading(false); }
  };

  const loadActivity = async (page = 0, filterOverride) => {
    setActivityLoading(true);
    const activeFilter = filterOverride !== undefined ? filterOverride : activityFilter;
    try {
      const params = new URLSearchParams({ limit: 30, page });
      if (activeFilter !== 'all') params.set('type', activeFilter);
      const res = await axios.get(API(`/api/settings/activity?${params}`), { headers: authHeader() });
      if (res.data.success) {
        setActivityEvents(res.data.data || []);
        setActivityTotal(res.data.total || 0);
        setActivityPage(page);
      }
    } catch (e) { console.error('[settings] activity load:', e.message); }
    finally { setActivityLoading(false); }
  };

  // ── Portal Config ──────────────────────────────────────────────────────────
  const loadPortalConfig = async () => {
    setPortalConfigLoading(true);
    try {
      const res = await axios.get(API('/api/settings/portal-config'), { headers: authHeader() });
      if (res.data.success) setPortalConfig(res.data.data);
    } catch (e) { console.error('[settings] portal config load:', e.message); }
    finally { setPortalConfigLoading(false); }
  };

  const savePortalConfig = async () => {
    if (!portalConfig) return;
    setPortalConfigSaving(true);
    try {
      const res = await axios.put(API('/api/settings/portal-config'), portalConfig, { headers: authHeader() });
      if (res.data.success) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch (e) { console.error('[settings] portal config save:', e.message); }
    finally { setPortalConfigSaving(false); }
  };

  const updatePortalSport = (sport, field, value) => {
    setPortalConfig(prev => ({ ...prev, [sport]: { ...prev[sport], [field]: value } }));
  };

  // ── Teams ────────────────────────────────────────────────────────────────────
  const fetchTeams = async (sport) => {
    if (!sport?.code) return;
    setLoadingTeams(true); setTeams([]);
    try {
      if (isNatStatBasketball(sport.code)) {
        const t = await fetchNatStatTeams(sport.code);
        if (t.length > 0) { setTeams(t.sort((a, b) => a.team_name.localeCompare(b.team_name))); return; }
      }
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData?.id) {
          const res = await axios.get(
            `${NODE_BACKEND_URL}/api/user/${userData.id}/sports/${sport.code}/teams`,
            { headers: authHeader() }
          );
          if (res.data?.success && Array.isArray(res.data.teams)) {
            setTeams(res.data.teams.map(t => ({
              team_id: String(t.TeamID), team_name: t.TeamName || t.Name || 'Unknown',
              full_name: t.FullName || t.TeamName || '', conference: t.Conference || '',
            })).sort((a, b) => a.team_name.localeCompare(b.team_name)));
          }
        }
      }
    } catch (e) { console.error('fetchTeams:', e); }
    finally { setLoadingTeams(false); }
  };

  const filteredTeams = useMemo(() => {
    if (!teamSearch) return teams;
    const s = teamSearch.toLowerCase();
    return teams.filter(t => t.team_name?.toLowerCase().includes(s) || t.conference?.toLowerCase().includes(s));
  }, [teams, teamSearch]);

  // ── Save (account/platform/evaluation) ────────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (role) localStorage.setItem('slam_user_role', role);
      if (selectedSport) {
        const matching = getTeamsBySportCode(selectedSport.code);
        await switchTeam(matching.length > 0 ? matching[0] : 'slamball');
      }
      await axios.post(API('/api/update-sport-team'), {
        id: user?.id,
        sport_type: selectedSport?.code,
        all_sports: selectedSport ? [selectedSport.code] : [],
        team_type: selectedTeam?.team_id,
        performance_criteria: performanceCriteria,
      }, { headers: authHeader() });
      if (setting) { setting.performance_criteria = performanceCriteria; sessionStorage.setItem('setting', JSON.stringify(setting)); }
      window.dispatchEvent(new CustomEvent('slamSettingsChanged', { detail: { sport: selectedSport, team: selectedTeam } }));
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      if (user?.id) getUserSettings(user.id);
    } catch (e) { console.error('Save error:', e); }
    finally { setIsSaving(false); }
  };

  const handleSaveAiModel = async () => {
    if (!aiModel) return;
    setIsSaving(true);
    try {
      const payload = { ...aiModel, performanceCriteria };
      const res = await axios.put(API('/api/settings/ai-model'), payload, { headers: authHeader() });
      if (res.data.success) { setAiModel(res.data.data); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    } catch (e) { console.error('AI model save:', e); }
    finally { setIsSaving(false); }
  };

  const handleSaveGovernance = async () => {
    if (!governance) return;
    setIsSaving(true);
    try {
      const res = await axios.put(API('/api/settings/governance'), governance, { headers: authHeader() });
      if (res.data.success) { setGovernance(res.data.data); setSaved(true); setTimeout(() => setSaved(false), 2500); }
    } catch (e) { console.error('Governance save:', e); }
    finally { setIsSaving(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const res = await axios.post(API('/api/settings/team/invite'), { email: inviteEmail, role: inviteRole, name: inviteName }, { headers: authHeader() });
      if (res.data.success) { setInviteEmail(''); setInviteName(''); loadTeam(); }
    } catch (e) { console.error('Invite:', e); }
    finally { setInviting(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try { await axios.put(API(`/api/settings/team/${userId}/role`), { role: newRole }, { headers: authHeader() }); loadTeam(); }
    catch (e) { console.error('Role change:', e); }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try { await axios.delete(API(`/api/settings/team/${userId}`), { headers: authHeader() }); loadTeam(); }
    catch (e) { console.error('Remove user:', e); }
  };

  const toggleFeatureFlag = (roleId, featureId) => {
    setGovernance(prev => ({
      ...prev,
      roles: {
        ...prev.roles,
        [roleId]: {
          ...prev.roles[roleId],
          features: {
            ...prev.roles[roleId]?.features,
            [featureId]: !prev.roles[roleId]?.features?.[featureId],
          },
        },
      },
    }));
  };

  const updatePositionWeight = (pos, attr, val) => {
    setAiModel(prev => ({
      ...prev,
      positions: { ...prev.positions, [pos]: { ...prev.positions[pos], [attr]: Number(val) } },
    }));
  };

  const updateThreshold = (key, val) => {
    setAiModel(prev => ({ ...prev, thresholds: { ...prev.thresholds, [key]: val } }));
  };

  const updateFeedbackWeight = (key, val) => {
    setAiModel(prev => ({ ...prev, feedbackWeights: { ...prev.feedbackWeights, [key]: Number(val) } }));
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  const wordCount = performanceCriteria.split(/\s+/).filter(Boolean).length;
  const isAD = role === 'Athletic Director' || localStorage.getItem('slam_user_role') === 'Athletic Director';

  const ROLE_COLOR_MAP = { amber: '#E8B800', blue: '#60A5FA', indigo: '#818CF8', green: '#4ADE80', purple: '#C084FC', zinc: '#71717A' };

  // ── Section Renderers ────────────────────────────────────────────────────────

  const renderAccount = () => (
    <div className="space-y-5">
      <div><p className={S.sectionTitle}>Account</p><p className={S.sectionSub}>Your identity on the SLAM platform</p></div>
      <div className={S.card}>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-[rgba(232,184,0,0.12)] border border-[rgba(232,184,0,0.25)] flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-bold text-[#E8B800]">{initials}</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{user?.name || 'Unknown User'}</p>
            <p className="text-xs text-[#555555] mt-0.5">{user?.email || ''}</p>
            {role && (
              <span className="mt-1.5 inline-block px-2 py-0.5 rounded-full bg-[rgba(232,184,0,0.1)] border border-[rgba(232,184,0,0.2)] text-[10px] text-[#E8B800] font-medium">{role}</span>
            )}
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className={S.label}>Email <span className="ml-2 inline-flex items-center gap-1 normal-case tracking-normal font-normal text-[#444444]"><Lock className="w-2.5 h-2.5" /> managed by your organization</span></label>
            <input className={`${S.input} opacity-40 cursor-not-allowed`} value={user?.email || ''} disabled readOnly />
          </div>
          <div>
            <label className={S.label}>Role</label>
            <select value={role} onChange={e => setRole(e.target.value)} className={S.input} style={{ appearance: 'none' }}>
              <option value="">Select your role...</option>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <p className="text-[10px] text-[#444444] mt-1.5">Stored locally on this device. Helps SLAM AI personalize responses.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlatform = () => (
    <div className="space-y-5">
      <div><p className={S.sectionTitle}>Platform</p><p className={S.sectionSub}>Your default sport and team power the AI context, NatStat queries, and dashboard theming across SLAM</p></div>
      <div className={S.card}>
        <label className={S.label}>Default Sport</label>
        <div className="grid grid-cols-2 gap-2">
          {SPORT_OPTIONS.map(sport => {
            const isActive = selectedSport?.code === sport.code;
            return (
              <button key={sport.code} onClick={() => { setSelectedSport(sport); setSelectedTeam(null); setTeamSearch(''); sessionStorage.setItem('selectedSport', JSON.stringify(sport)); sessionStorage.removeItem('selectedTeam'); fetchTeams(sport); }}
                className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${isActive ? 'bg-[rgba(232,184,0,0.12)] border-[rgba(232,184,0,0.4)] text-[#E8B800]' : 'bg-[#111111] border-[#1A1A1A] text-[#888888] hover:border-[#2A2A2A] hover:text-white'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-base">{sport.emoji}</span>
                  <div><div className="font-mono text-[10px] opacity-60">{sport.code}</div><div className="font-medium text-xs mt-0.5">{sport.name}</div></div>
                </div>
              </button>
            );
          })}
        </div>
        {selectedSport && <div className="mt-3 px-3 py-2 rounded-lg bg-[rgba(232,184,0,0.07)] border border-[rgba(232,184,0,0.15)]"><p className="text-xs text-[#E8B800]/80">Active: <span className="font-semibold">{selectedSport.name}</span></p></div>}
      </div>
      {selectedSport && (
        <div className={S.card}>
          <label className={S.label}>Default Team — {selectedSport.name}</label>
          {loadingTeams ? (
            <div className="flex items-center gap-2 py-5 text-[#555555]"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading teams…</span></div>
          ) : (
            <>
              <div className="relative mb-3"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#555555] pointer-events-none" /><input type="text" placeholder="Search teams or conference…" value={teamSearch} onChange={e => setTeamSearch(e.target.value)} className={`${S.input} pl-9`} /></div>
              <div className="rounded-lg border border-[#1A1A1A] divide-y divide-[#111111] max-h-60 overflow-y-auto">
                {filteredTeams.length > 0 ? filteredTeams.map(team => {
                  const isActive = selectedTeam?.team_id === team.team_id;
                  return (
                    <button key={team.team_id} onClick={() => { setSelectedTeam(team); sessionStorage.setItem('selectedTeam', JSON.stringify(team)); }}
                      className={`w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors ${isActive ? 'bg-[rgba(232,184,0,0.08)]' : 'hover:bg-[rgba(255,255,255,0.03)]'}`}>
                      <div><p className={`text-sm font-medium ${isActive ? 'text-[#E8B800]' : 'text-white'}`}>{team.team_name}</p>{team.conference && <p className="text-[10px] text-[#555555] mt-0.5">{team.conference}</p>}</div>
                      {isActive && <CheckCircle className="w-4 h-4 text-[#E8B800] flex-shrink-0" />}
                    </button>
                  );
                }) : <div className="px-4 py-6 text-center text-sm text-[#555555]">No teams found</div>}
              </div>
              {selectedTeam && <div className="mt-3 px-3 py-2 rounded-lg bg-[rgba(232,184,0,0.07)] border border-[rgba(232,184,0,0.15)]"><p className="text-xs text-[#E8B800]/80">Active: <span className="font-semibold">{selectedTeam.team_name}</span>{selectedTeam.conference && <span className="text-[#666666] font-normal"> · {selectedTeam.conference}</span>}</p></div>}
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderNavigation = () => (
    <div className="space-y-5">
      <div><p className={S.sectionTitle}>Navigation</p><p className={S.sectionSub}>Customize your sidebar — hide, show, and reorder items</p></div>
      <div className={S.card}><NavigationSettings isDarkMode={true} /></div>
    </div>
  );

  const renderEvaluation = () => (
    <div className="space-y-5">
      <div><p className={S.sectionTitle}>Evaluation Standards</p><p className={S.sectionSub}>Define your criteria — injected into every SLAM AI report, portal assessment, and scouting recommendation</p></div>
      <div className={S.card}>
        <label className={S.label}>Performance Criteria</label>
        <p className="text-xs text-[#555555] mb-3 leading-relaxed">Include athletic thresholds, academic requirements, character standards, eligibility criteria, and physical minimums.</p>
        <textarea value={performanceCriteria} onChange={e => setPerformanceCriteria(e.target.value)}
          maxLength={2000}
          placeholder={`e.g.\n• Minimum 3.0 GPA, no academic restrictions\n• 2+ years of eligibility remaining\n• No transfer restrictions or waivers pending\n• Minimum 6'2" for wings, 6'8" for forwards\n• Strong character references from previous head coach\n• Prioritize players from high-major or mid-major conferences`}
          className={`${S.input} min-h-[220px] resize-none leading-relaxed`} />
        <div className="mt-3 flex items-center justify-between">
          <p className="text-[10px] text-[#555555]">{wordCount > 0 ? `${wordCount} word${wordCount !== 1 ? 's' : ''} · applied to all AI evaluations` : ''}</p>
          <p className="text-[10px] text-[#444444]">{performanceCriteria.length}/2000</p>
          {wordCount > 0 && <button onClick={() => setPerformanceCriteria('')} className="text-[10px] text-[#444444] hover:text-[#888888] transition-colors">Clear</button>}
        </div>
      </div>
      <div className="px-4 py-3 rounded-xl border border-[#1A1A1A] bg-[#080808]">
        <p className="text-[10px] text-[#444444] leading-relaxed"><span className="text-[#666666] font-semibold">Where this is used: </span>Transfer Portal fit scores, Ask SLAM AI player evaluations, GM Dashboard prospect rankings, and any AI-generated scouting report.</p>
      </div>
    </div>
  );

  const renderAiModel = () => {
    if (aiModelLoading) return <LoadingState label="Loading AI model config..." />;
    if (!aiModel) return <ErrorState label="Could not load AI model config" onRetry={loadAiModel} />;
    return (
      <div className="space-y-6">
        <div><p className={S.sectionTitle}>AI Model Configuration</p><p className={S.sectionSub}>Position weights, evaluation thresholds, and feedback loop parameters injected into Vertex AI prompts</p></div>

        {/* Position Weights */}
        <div className={S.card}>
          <label className={S.label}>Position Attribute Weights (0–100)</label>
          <p className="text-xs text-[#555555] mb-4">These weights guide SLAM AI when evaluating players by position. Higher = more important for that role.</p>
          <div className="space-y-5">
            {POSITIONS.map(pos => (
              <div key={pos}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-white font-mono">{pos}</span>
                  {aiModel.gaps?.[pos] && <span className={`${S.pill} bg-red-900/30 text-red-400 border border-red-900/40`}>Gap Position</span>}
                </div>
                <div className="space-y-1.5">
                  {POSITION_ATTRS.map(attr => (
                    <div key={attr} className="flex items-center gap-3">
                      <span className="text-[10px] text-[#666666] w-20 capitalize">{attr}</span>
                      <input type="range" min={0} max={100} value={aiModel.positions?.[pos]?.[attr] ?? 50}
                        onChange={e => updatePositionWeight(pos, attr, e.target.value)}
                        className="flex-1 h-1 accent-[#E8B800] cursor-pointer" />
                      <span className="text-[10px] text-[#E8B800] w-6 text-right font-mono">{aiModel.positions?.[pos]?.[attr] ?? 50}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gap Positions */}
        <div className={S.card}>
          <label className={S.label}>Roster Gaps</label>
          <p className="text-xs text-[#555555] mb-4">Mark positions where you need players. SLAM AI prioritizes these when ranking portal targets.</p>
          <div className="flex flex-wrap gap-2">
            {POSITIONS.map(pos => {
              const isGap = aiModel.gaps?.[pos];
              return (
                <button key={pos} onClick={() => setAiModel(prev => ({ ...prev, gaps: { ...prev.gaps, [pos]: !prev.gaps?.[pos] } }))}
                  className={`px-4 py-2 rounded-lg border text-xs font-mono font-semibold transition-all ${isGap ? 'bg-red-900/30 border-red-500/50 text-red-400' : 'bg-[#111111] border-[#222] text-[#666] hover:border-[#444] hover:text-white'}`}>
                  {pos} {isGap ? '· NEED' : ''}
                </button>
              );
            })}
          </div>
        </div>

        {/* Thresholds */}
        <div className={S.card}>
          <label className={S.label}>Minimum Thresholds</label>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-[#666] uppercase tracking-widest block mb-1">Minimum GPA</label>
              <input type="number" min={0} max={4} step={0.1} value={aiModel.thresholds?.gpa ?? 2.8}
                onChange={e => updateThreshold('gpa', parseFloat(e.target.value))}
                className={`${S.input} w-28`} />
            </div>
            <div>
              <label className="text-[10px] text-[#666] uppercase tracking-widest block mb-1">Eligibility Years Remaining (minimum)</label>
              <input type="number" min={0} max={5} step={1} value={aiModel.thresholds?.eligibilityYears ?? 1}
                onChange={e => updateThreshold('eligibilityYears', parseInt(e.target.value))}
                className={`${S.input} w-28`} />
            </div>
            <div>
              <label className="text-[10px] text-[#666] uppercase tracking-widest block mb-1">Minimum Conference Level</label>
              <select value={aiModel.thresholds?.conferenceLevel || 'mid-major'}
                onChange={e => updateThreshold('conferenceLevel', e.target.value)}
                className={`${S.input} w-48`} style={{ appearance: 'none' }}>
                <option value="any">Any Conference</option>
                <option value="juco">JUCO+</option>
                <option value="low-major">Low Major+</option>
                <option value="mid-major">Mid Major+</option>
                <option value="high-major">High Major Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Feedback Loop Weights */}
        <div className={S.card}>
          <label className={S.label}>Feedback Loop Weights</label>
          <p className="text-xs text-[#555555] mb-4">How much each feedback source influences AI model behavior. Must sum to 100.</p>
          {feedbackStats && (
            <div className="grid grid-cols-3 gap-3 mb-5">
              <FeedbackCard label="Advanced Scouting" stats={feedbackStats.pitchTipping} color="#E8B800" />
              <FeedbackCard label="Ask SLAM AI"   stats={feedbackStats.askSlamAI}    color="#60A5FA" />
              <FeedbackCard label="Scouting"      stats={feedbackStats.scoutingReports} color="#4ADE80" />
            </div>
          )}
          <div className="space-y-4">
            {[
              { key: 'pitchTipping',    label: 'Advanced Scouting Approve/Deny', color: '#E8B800' },
              { key: 'scoutingReports', label: 'Scouting Report Ratings',    color: '#4ADE80' },
              { key: 'askSlamAI',       label: 'Ask SLAM AI Quality',        color: '#60A5FA' },
            ].map(({ key, label, color }) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-xs text-[#888] w-44">{label}</span>
                <input type="range" min={0} max={100} value={aiModel.feedbackWeights?.[key] ?? 33}
                  onChange={e => updateFeedbackWeight(key, e.target.value)}
                  className="flex-1 h-1 cursor-pointer" style={{ accentColor: color }} />
                <span className="text-xs font-mono w-8 text-right" style={{ color }}>{aiModel.feedbackWeights?.[key] ?? 33}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Export Training Data */}
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#1A1A1A] bg-[#080808]">
          <div>
            <p className="text-xs font-semibold text-white">Export Training Data</p>
            <p className="text-[10px] text-[#555555] mt-0.5">Download position weights, criteria, and feedback events for Vertex AI fine-tuning</p>
          </div>
          <a href={API('/api/settings/export-training-data')} download className={`${S.btn} ${S.btnGhost}`}><Download className="w-3.5 h-3.5" /> Export</a>
        </div>

        <button onClick={handleSaveAiModel} className={`${S.btn} ${S.btnGold} w-full justify-center`} disabled={isSaving}>
          {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save AI Model Config
        </button>
      </div>
    );
  };

  const renderTeam = () => {
    if (teamLoading) return <LoadingState label="Loading team roster..." />;
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div><p className={S.sectionTitle}>Team Management</p><p className={S.sectionSub}>Manage staff access, roles, and invitations for your organization</p></div>
          <button onClick={loadTeam} className={`${S.btn} ${S.btnGhost}`}><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>

        {/* Invite new member */}
        <div className={S.card}>
          <label className={S.label}>Invite Staff Member</label>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#555] block mb-1">Name</label>
                <input placeholder="Full name" value={inviteName} onChange={e => setInviteName(e.target.value)} className={S.input} />
              </div>
              <div>
                <label className="text-[10px] text-[#555] block mb-1">Email</label>
                <input type="email" placeholder="coach@university.edu" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className={S.input} />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[#555] block mb-1">Role</label>
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className={S.input} style={{ appearance: 'none' }}>
                {GOVERNANCE_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <button onClick={handleInvite} disabled={inviting || !inviteEmail} className={`${S.btn} ${S.btnGold} w-full justify-center`}>
              {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Send Invitation
            </button>
          </div>
        </div>

        {/* Pending invites */}
        {pendingInvites.length > 0 && (
          <div className={S.card}>
            <label className={S.label}>Pending Invitations ({pendingInvites.length})</label>
            <div className="space-y-2">
              {pendingInvites.map(invite => (
                <div key={invite.id} className="flex items-center justify-between py-2 border-b border-[#111] last:border-0">
                  <div>
                    <p className="text-sm text-white">{invite.name || invite.email}</p>
                    <p className="text-[10px] text-[#555]">{invite.email} · <span className="text-[#888]">{GOVERNANCE_ROLES.find(r => r.id === invite.role)?.label || invite.role}</span></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`${S.pill} bg-amber-900/20 text-amber-400 border border-amber-900/30`}>Pending</span>
                    <button onClick={() => handleRemoveUser(invite.id)} className={`${S.btn} ${S.btnDanger} px-2 py-1`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active team members */}
        <div className={S.card}>
          <label className={S.label}>Active Staff ({teamMembers.length})</label>
          {teamMembers.length === 0 ? (
            <div className="py-8 text-center">
              <Users className="w-8 h-8 text-[#333] mx-auto mb-2" />
              <p className="text-sm text-[#555]">No team members found</p>
              <p className="text-[10px] text-[#444] mt-1">Connect to the live database to see staff</p>
            </div>
          ) : (
            <div className="space-y-1">
              {teamMembers.map(member => {
                const govRole = GOVERNANCE_ROLES.find(r => r.label === member.role || r.id === member.role);
                const roleColor = govRole ? ROLE_COLOR_MAP[govRole.color] : '#888';
                return (
                  <div key={member.id} className="flex items-center justify-between py-2.5 border-b border-[#0F0F0F] last:border-0 group">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-xs font-bold text-[#888]">
                        {member.name?.slice(0, 2).toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm text-white">{member.name || 'Unnamed'}</p>
                        <p className="text-[10px] text-[#555]">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={member.role || ''} onChange={e => handleRoleChange(member.id, e.target.value)}
                        className="bg-[#111] border border-[#222] rounded-lg px-2 py-1 text-xs text-white outline-none focus:border-[rgba(232,184,0,0.4)]" style={{ appearance: 'none', color: roleColor }}>
                        {GOVERNANCE_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                      <button onClick={() => handleRemoveUser(member.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-red-500/60 hover:text-red-400 hover:bg-red-900/20">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGovernance = () => {
    if (governanceLoading) return <LoadingState label="Loading governance config..." />;
    if (!governance) return <ErrorState label="Could not load governance config" onRetry={loadGovernance} />;

    return (
      <div className="space-y-6">
        <div><p className={S.sectionTitle}>Role Governance</p><p className={S.sectionSub}>Control which features each staff tier can access. Changes affect all users in that role immediately.</p></div>

        <div className={S.card}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left text-[10px] text-[#555] uppercase tracking-widest pb-3 w-36">Feature</th>
                  {GOVERNANCE_ROLES.map(govRole => {
                    const roleColor = ROLE_COLOR_MAP[govRole.color];
                    return (
                      <th key={govRole.id} className="pb-3 px-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${roleColor}20`, border: `1px solid ${roleColor}40` }}>
                            <govRole.icon className="w-3 h-3" style={{ color: roleColor }} />
                          </div>
                          <span className="text-[9px] font-semibold" style={{ color: roleColor }}>{govRole.id === 'AD' ? 'AD' : govRole.label.replace(' Coach', '').replace('Assistant ', 'Asst.')}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0F0F0F]">
                {FEATURE_FLAGS.map(feature => (
                  <tr key={feature.id} className="hover:bg-[rgba(255,255,255,0.01)]">
                    <td className="py-2.5 pr-4">
                      <p className="text-white font-medium text-xs">{feature.label}</p>
                      <p className="text-[#444] text-[10px] mt-0.5">{feature.desc}</p>
                    </td>
                    {GOVERNANCE_ROLES.map(govRole => {
                      const enabled = governance.roles?.[govRole.id]?.features?.[feature.id];
                      const isAdOnly = feature.id === 'teamManagement' || feature.id === 'governance' || feature.id === 'aiModel';
                      const locked = govRole.id === 'AD' && isAdOnly;
                      return (
                        <td key={govRole.id} className="py-2.5 px-2 text-center">
                          <button onClick={() => !locked && toggleFeatureFlag(govRole.id, feature.id)}
                            disabled={locked}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center mx-auto transition-all ${locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:scale-110'} ${enabled ? 'bg-[rgba(232,184,0,0.15)] border border-[rgba(232,184,0,0.4)]' : 'bg-[#111] border border-[#1A1A1A]'}`}>
                            {locked ? <Lock className="w-3 h-3 text-[#E8B800]" /> : enabled ? <Eye className="w-3 h-3 text-[#E8B800]" /> : <EyeOff className="w-3 h-3 text-[#333]" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[10px] text-[#444]"><Eye className="w-3 h-3 inline mr-1 text-[#E8B800]" /> = Allowed &nbsp;·&nbsp; <EyeOff className="w-3 h-3 inline mr-1 text-[#333]" /> = Hidden &nbsp;·&nbsp; <Lock className="w-3 h-3 inline mr-1 text-[#E8B800]" /> = AD always enabled</p>
          <button onClick={handleSaveGovernance} className={`${S.btn} ${S.btnGold}`} disabled={isSaving}>
            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Governance
          </button>
        </div>
      </div>
    );
  };

  const ACTIVITY_TYPE_LABELS = {
    ai_query: 'Ask SLAM AI', ai_feedback: 'AI Feedback', page_view: 'Page View',
    scouting_report_generated: 'Scouting Report', scouting_report_rated: 'Report Rating',
    team_invite: 'Team Invite', governance_change: 'Governance', video_analyzed: 'Video Analysis',
    fit_score_run: 'Fit Score', export: 'Data Export',
  };

  const ACTIVITY_ICONS = {
    ai_query: Brain, ai_feedback: ThumbsUp, page_view: Eye,
    scouting_report_generated: Target, team_invite: Mail,
    governance_change: Shield, video_analyzed: Activity, fit_score_run: TrendingUp,
  };

  const renderActivity = () => (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div><p className={S.sectionTitle}>Activity Timeline</p><p className={S.sectionSub}>Your full session history — pages visited, features used, AI queries, and feedback events</p></div>
        <button onClick={() => loadActivity(0)} className={`${S.btn} ${S.btnGhost}`}><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        {['all', 'ai_query', 'ai_feedback', 'page_view', 'scouting_report_generated', 'team_invite', 'governance_change'].map(type => (
          <button key={type} onClick={() => { setActivityFilter(type); loadActivity(0, type); }}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${activityFilter === type ? 'bg-[rgba(232,184,0,0.15)] text-[#E8B800] border border-[rgba(232,184,0,0.3)]' : 'bg-[#0F0F0F] text-[#555] border border-[#1A1A1A] hover:text-[#888]'}`}>
            {type === 'all' ? 'All Events' : (ACTIVITY_TYPE_LABELS[type] || type)}
          </button>
        ))}
      </div>

      <div className={S.card}>
        {activityLoading ? (
          <div className="flex items-center gap-2 py-8 justify-center text-[#555]"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Loading events...</span></div>
        ) : activityEvents.length === 0 ? (
          <div className="py-10 text-center">
            <Activity className="w-8 h-8 text-[#2A2A2A] mx-auto mb-2" />
            <p className="text-sm text-[#555]">No activity recorded yet</p>
            <p className="text-[10px] text-[#444] mt-1">Events are logged as you use the platform</p>
          </div>
        ) : (
          <div className="space-y-0">
            {activityEvents.map((event, idx) => {
              const IconComp = ACTIVITY_ICONS[event.type] || Clock;
              const label = ACTIVITY_TYPE_LABELS[event.type] || event.type;
              const time = event.serverTimestamp || event.timestamp;
              const date = time ? new Date(time) : null;
              return (
                <div key={idx} className="flex items-start gap-3 py-2.5 border-b border-[#0F0F0F] last:border-0">
                  <div className="w-6 h-6 rounded-lg bg-[#111] border border-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <IconComp className="w-3 h-3 text-[#666]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-white">{label}</span>
                      {event.type === 'ai_feedback' && (
                        <span className={`${S.pill} ${event.value === 'helpful' ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'}`}>
                          {event.value === 'helpful' ? '👍' : '👎'}
                        </span>
                      )}
                    </div>
                    {event.page && <p className="text-[10px] text-[#444] mt-0.5">{event.page}</p>}
                    {event.message && <p className="text-[10px] text-[#555] mt-0.5 truncate">{event.message?.slice(0, 80)}</p>}
                    {event.email && <p className="text-[10px] text-[#555] mt-0.5">{event.email} · {event.role}</p>}
                  </div>
                  <div className="text-[10px] text-[#444] flex-shrink-0">
                    {date ? `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {activityTotal > 30 && (
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-[#1A1A1A]">
            <span className="text-[10px] text-[#555]">{activityTotal} total events</span>
            <div className="flex gap-2">
              <button onClick={() => loadActivity(Math.max(0, activityPage - 1))} disabled={activityPage === 0} className={`${S.btn} ${S.btnGhost} disabled:opacity-40`}>Prev</button>
              <span className="text-xs text-[#555] self-center px-2">Page {activityPage + 1}</span>
              <button onClick={() => loadActivity(activityPage + 1)} disabled={(activityPage + 1) * 30 >= activityTotal} className={`${S.btn} ${S.btnGhost} disabled:opacity-40`}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ── Portal Config Renderer ─────────────────────────────────────────────────
  const renderPortalConfig = () => {
    if (portalConfigLoading) return <LoadingState label="Loading portal config..." />;
    if (!portalConfig) return <ErrorState label="Failed to load portal config" onRetry={loadPortalConfig} />;

    const renderSportConfig = (sport, label) => {
      const cfg = portalConfig[sport] || {};
      return (
        <div key={sport} className="bg-[#111] border border-[#1A1A1A] rounded-xl p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-white">{label}</p>
            <button
              onClick={() => updatePortalSport(sport, 'enabled', !cfg.enabled)}
              className="flex items-center gap-1.5 text-xs"
            >
              {cfg.enabled ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4 text-[#444]" />}
              <span className={cfg.enabled ? 'text-emerald-500' : 'text-[#444]'}>{cfg.enabled ? 'Active' : 'Disabled'}</span>
            </button>
          </div>

          <label className={S.label}>Culture Context</label>
          <textarea
            value={cfg.cultureContext || ''}
            onChange={(e) => updatePortalSport(sport, 'cultureContext', e.target.value)}
            placeholder="Describe your program's culture, values, and what you look for in transfers..."
            rows={3}
            className={`${S.input} resize-y`}
            maxLength={2000}
          />
          <p className="text-[9px] text-[#333] mt-1 mb-3">{(cfg.cultureContext || '').length}/2000 — injected into AI portal evaluations</p>

          <label className={S.label}>Stats & Scheme Context</label>
          <textarea
            value={cfg.statsContext || ''}
            onChange={(e) => updatePortalSport(sport, 'statsContext', e.target.value)}
            placeholder={sport === 'baseball'
              ? 'Describe your stats priorities (ERA, OPS, K rate), offensive/defensive philosophy...'
              : 'Describe your stats priorities (PER, 3PT%, AST/TO), offensive/defensive system...'}
            rows={3}
            className={`${S.input} resize-y`}
            maxLength={2000}
          />
          <p className="text-[9px] text-[#333] mt-1 mb-3">{(cfg.statsContext || '').length}/2000 — shapes portal player research</p>

          <label className={S.label}>Conference Filter</label>
          <input
            type="text"
            value={cfg.conferenceFilter || ''}
            onChange={(e) => updatePortalSport(sport, 'conferenceFilter', e.target.value)}
            placeholder="e.g., ACC, Big 12, SEC (leave empty for all)"
            className={S.input}
            maxLength={100}
          />
        </div>
      );
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className={S.sectionTitle}>Transfer Portal Configuration</p>
            <p className={S.sectionSub}>Culture and stats context injected into every portal AI evaluation and player research</p>
          </div>
          <button onClick={savePortalConfig} disabled={portalConfigSaving}
            className={`${S.btn} ${S.btnGold} disabled:opacity-50`}>
            {portalConfigSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Portal Config
          </button>
        </div>
        {renderSportConfig('baseball', 'College Baseball Portal')}
        {renderSportConfig('basketball', 'College Basketball Portal')}
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'account':    return renderAccount();
      case 'platform':   return renderPlatform();
      case 'navigation': return renderNavigation();
      case 'evaluation': return renderEvaluation();
      case 'ai-model':   return renderAiModel();
      case 'team':       return renderTeam();
      case 'governance': return renderGovernance();
      case 'portal':     return renderPortalConfig();
      case 'activity':   return renderActivity();
      default:           return null;
    }
  };

  const needsGlobalSave = ['account', 'platform', 'evaluation'].includes(activeSection);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-black text-white overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-[#1A1A1A] px-5 py-3 bg-[#0A0A0A]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-[rgba(232,184,0,0.12)] border border-[rgba(232,184,0,0.2)] flex items-center justify-center">
              <Settings className="w-3.5 h-3.5 text-[#E8B800]" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-white leading-none">Settings</h1>
              <p className="text-[10px] text-[#444444] mt-0.5">SLAM Platform Configuration</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {saved && <div className="flex items-center gap-1.5 text-emerald-500 text-xs"><CheckCircle className="w-3.5 h-3.5" /> Saved</div>}
            {needsGlobalSave && (
              <button onClick={handleSave} disabled={isSaving}
                className={`${S.btn} ${S.btnGold} disabled:opacity-50 disabled:cursor-not-allowed`}>
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left nav */}
        <nav className="w-44 flex-shrink-0 border-r border-[#1A1A1A] bg-[#080808] py-4 px-2 space-y-0.5 overflow-y-auto">
          {NAV_SECTIONS.map(({ id, label: navLabel, icon: Icon }) => {
            const isActive = activeSection === id;
            const isAdGated = (id === 'team' || id === 'governance');
            return (
              <button key={id} onClick={() => setActiveSection(id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors text-left ${isActive ? 'bg-[rgba(232,184,0,0.1)] text-[#E8B800]' : 'text-[#555555] hover:text-[#AAAAAA] hover:bg-[rgba(255,255,255,0.03)]'}`}>
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{navLabel}</span>
                {isActive ? <ChevronRight className="w-3 h-3 ml-auto opacity-60" /> : isAdGated ? <Crown className="w-2.5 h-2.5 ml-auto text-[#E8B800]/40" /> : null}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-2xl">
            {renderSection()}
          </div>
        </main>
      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const LoadingState = ({ label }) => (
  <div className="flex items-center gap-3 py-16 justify-center text-[#555]">
    <Loader2 className="w-5 h-5 animate-spin" />
    <span className="text-sm">{label}</span>
  </div>
);

const ErrorState = ({ label, onRetry }) => (
  <div className="flex flex-col items-center gap-3 py-16 text-center">
    <AlertCircle className="w-6 h-6 text-red-500/60" />
    <p className="text-sm text-[#555]">{label}</p>
    <button onClick={onRetry} className="text-xs text-[#E8B800] hover:text-[#f0c000] flex items-center gap-1.5"><RefreshCw className="w-3 h-3" /> Retry</button>
  </div>
);

const FeedbackCard = ({ label, stats = {}, color }) => {
  const rate = stats.approvalRate ?? stats.helpfulRate ?? 0;
  const total = stats.total ?? stats.totalQueries ?? 0;
  return (
    <div className="bg-[#111] border border-[#1A1A1A] rounded-lg p-3">
      <p className="text-[10px] text-[#555] mb-1">{label}</p>
      <p className="text-xl font-bold font-mono" style={{ color }}>{rate}%</p>
      <p className="text-[10px] text-[#444] mt-0.5">{total} total</p>
      <div className="mt-2 h-1 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${rate}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

export default SLAMSettings;
